import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import App from "elements/HtmlEditor/App";

type StructuredItem = {
  contenido_id?: number;
  fiesta_id?: number;
  lang_id: number;
  scope: string;
  variant_key: string;
  section_key: string;
  option_order: number;
  content_html?: string;
  content_json?: any;
  citation?: string | null;
  rubric?: string | null;
  source_key?: string | null;
  source_locator?: string | null;
  status?: "draft" | "verified";
};

type Props = {
  commonMeta: any;
  structuredContent: any;
  langId: string;
  items: StructuredItem[];
  onItemsChange: (items: StructuredItem[]) => void;
};

const EMPTY_STATUS: "draft" = "draft";

export default function StructuredCommonsEditor({
  commonMeta,
  structuredContent,
  langId,
  items,
  onItemsChange,
}: Props) {
  const [variantKey, setVariantKey] = useState("default");
  const initializedVariantContext = useRef<string>("");
  const [massFormularyOrder, setMassFormularyOrder] = useState(1);

  const model = structuredContent?.model || commonMeta?.content_model || null;
  const ready = structuredContent?.ready === true;
  const languageId = parseInt(langId || "1", 10);
  const lectionaryReferenceSet = commonMeta?.lectionary_reference_set || null;

  // Todas las filas estructuradas, incluido el Leccionario, conservan
  // variant_key. El visor público también lo usa para agrupar y rotular.
  const variantForScope = (_scope: string) => variantKey;

  const expectedReference = (
    scope: string,
    sectionKey: string,
    optionOrder: number
  ) => {
    // El mapa OLM/CEC del catálogo es la referencia maestra española.
    // Nunca se precarga como si fuera la edición bíblica de otro idioma.
    if (
      languageId !== 1
      || scope !== "lectionary"
      || !lectionaryReferenceSet
    ) {
      return null;
    }
    const options = lectionaryReferenceSet?.sections?.[sectionKey] || [];
    return options[optionOrder - 1] || null;
  };

  const sources = useMemo(() => {
    const result: Record<string, any> = {};
    (commonMeta?.sources || []).forEach((source: any) => {
      if (source?.key) result[source.key] = source;
    });
    return result;
  }, [commonMeta]);

  const variants = useMemo(() => {
    const values: Array<{ key: string; label: string }> = [
      { key: "default", label: "Texto general del común" },
    ];
    const seen = new Set<string>(["default"]);

    const add = (variant: any, fallbackLabel?: string | null) => {
      const key = String(
        typeof variant === "string" ? variant : variant?.key || ""
      ).trim();
      if (!key || seen.has(key)) return;

      const label = String(
        typeof variant === "object"
          ? variant?.label || fallbackLabel || ""
          : fallbackLabel || ""
      ).trim();

      seen.add(key);
      values.push({ key, label: label || key.replace(/_/g, " ") });
    };

    if (commonMeta?.variant_key) {
      add({ key: commonMeta.variant_key }, commonMeta?.variant_label);
    }
    (commonMeta?.official_variants || []).forEach((variant: any) => add(variant));

    // Blindaje de cobertura: si una fila visible ya existe con una variante
    // válida importada, debe poder seleccionarse aunque el catálogo cambie
    // posteriormente su presentación.
    items
      .filter((item) => Number(item.lang_id) === languageId)
      .forEach((item) => add(item.variant_key));

    return values;
  }, [commonMeta, items, languageId]);

  useEffect(() => {
    const contextKey = `${commonMeta?.fiesta_id || "none"}:${languageId}`;
    const languageItems = items.filter(
      (item) => Number(item.lang_id) === languageId
    );

    // Esperar a las filas reales. Antes podía inicializarse con items=[]
    // y quedarse en una variante contenedora sin contenido.
    if (languageItems.length === 0) {
      return;
    }

    // Inicializar una sola vez por Común+idioma para no pisar después
    // la selección manual del administrador.
    if (initializedVariantContext.current === contextKey) {
      return;
    }

    const existingVariants = Array.from(
      new Set(
        languageItems
          .map((item) => String(item.variant_key || "default").trim() || "default")
      )
    );

    const variantsWithVisibleContent = Array.from(
      new Set(
        languageItems
          .filter((item) => {
            const html = String(item.content_html || "").trim();
            return html !== "" || item.content_json != null;
          })
          .map((item) => String(item.variant_key || "default").trim() || "default")
      )
    );

    const recordVariant = String(commonMeta?.variant_key || "").trim();
    const preferred =
      recordVariant && variantsWithVisibleContent.includes(recordVariant)
        ? recordVariant
        : variantsWithVisibleContent[0]
          || existingVariants[0]
          || recordVariant
          || "default";

    initializedVariantContext.current = contextKey;
    setVariantKey(preferred);
    setMassFormularyOrder(1);
  }, [
    commonMeta?.fiesta_id,
    commonMeta?.variant_key,
    languageId,
    items,
  ]);

  if (!model) {
    return null;
  }

  if (!ready) {
    return (
      <div style={{ margin: "24px 0", padding: 16, border: "1px solid #ddd" }}>
        <strong>Contenido estructurado pendiente de activación</strong>
        <div style={{ marginTop: 6 }}>
          La Misa y el Leccionario ya tienen un modelo canónico definido, pero
          la estructura de base de datos aún no ha sido activada.
        </div>
      </div>
    );
  }

  const currentItems = items.filter(
    (item) => Number(item.lang_id) === languageId
  );

  const familyScopes = new Set<string>(
    Array.isArray(commonMeta?.scope) ? commonMeta.scope : []
  );
  const scopeEnabled = (scope: string) =>
    familyScopes.has(scope)
    || currentItems.some((item) => item.scope === scope);

  const nativeExistingMassOrders = currentItems
    .filter(
      (item) =>
        item.scope === "mass"
        && item.variant_key === variantKey
    )
    .map((item) => Number(item.option_order) || 1);

  // Los conteos colombianos solo gobiernan el español. En otro idioma,
  // la estructura visible se deriva de las filas de su propia edición.
  const massFormularyCount =
    languageId === 1
      ? Math.max(
          1,
          Number(commonMeta?.mass_formularies?.[variantKey] || 1)
        )
      : Math.max(1, ...nativeExistingMassOrders);

  const findItem = (
    scope: string,
    sectionKey: string,
    optionOrder: number
  ): StructuredItem | undefined =>
    currentItems.find(
      (item) =>
        item.scope === scope &&
        item.variant_key === variantForScope(scope) &&
        item.section_key === sectionKey &&
        Number(item.option_order) === optionOrder
    );

  const patchItem = (
    scope: string,
    sectionKey: string,
    optionOrder: number,
    patch: Partial<StructuredItem>
  ) => {
    const index = items.findIndex(
      (item) =>
        Number(item.lang_id) === languageId &&
        item.scope === scope &&
        item.variant_key === variantForScope(scope) &&
        item.section_key === sectionKey &&
        Number(item.option_order) === optionOrder
    );

    const reference = expectedReference(scope, sectionKey, optionOrder);
    const base: StructuredItem =
      index >= 0
        ? items[index]
        : {
            lang_id: languageId,
            scope,
            variant_key: variantForScope(scope),
            section_key: sectionKey,
            option_order: optionOrder,
            content_html: "",
            content_json: null,
            citation: reference?.citation || "",
            rubric: reference?.rubric || "",
            source_key: "",
            source_locator: "",
            status: EMPTY_STATUS,
          };

    const next = [...items];
    const value = { ...base, ...patch };

    if (index >= 0) {
      next[index] = value;
    } else {
      next.push(value);
    }

    onItemsChange(next);
  };

  const sectionOptions = (scope: string, sectionKey: string) => {
    const existing = currentItems
      .filter(
        (item) =>
          item.scope === scope &&
          item.variant_key === variantForScope(scope) &&
          item.section_key === sectionKey
      )
      .map((item) => Number(item.option_order));

    const expectedCount =
      languageId === 1 && scope === "lectionary"
        ? (lectionaryReferenceSet?.sections?.[sectionKey] || []).length
        : 0;

    const expected = Array.from(
      { length: expectedCount },
      (_, index) => index + 1
    );

    const merged = Array.from(new Set([...expected, ...existing]))
      .sort((a, b) => a - b);

    return merged.length > 0 ? merged : [1];
  };

  const allowedSources = (scope: string) => {
    const keys = model?.scopes?.[scope]?.source_keys || [];
    return keys
      .map((key: string) => sources[key])
      .filter(Boolean)
      .filter((source: any) => source?.verified_content_allowed !== false)
      .filter((source: any) => {
        const sourceLangId = Number(source?.lang_id || 0);
        return sourceLangId > 0
          ? sourceLangId === languageId
          : languageId === 1;
      });
  };

  const metadataFields = (
    scope: string,
    sectionKey: string,
    optionOrder: number
  ) => {
    const item = findItem(scope, sectionKey, optionOrder);
    const reference = expectedReference(scope, sectionKey, optionOrder);
    const sourceOptions = allowedSources(scope);

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 12,
          margin: "12px 0 28px",
        }}
      >
        <TextField
          label={scope === "lectionary" ? "Referencia bíblica" : "Cita / referencia"}
          value={item?.citation || reference?.citation || ""}
          onChange={(e) =>
            patchItem(scope, sectionKey, optionOrder, {
              citation: e.target.value,
            })
          }
          fullWidth
        />

        <FormControl fullWidth>
          <InputLabel>Fuente aprobada</InputLabel>
          <Select
            label="Fuente aprobada"
            value={item?.source_key || ""}
            onChange={(e) =>
              patchItem(scope, sectionKey, optionOrder, {
                source_key: String(e.target.value || ""),
              })
            }
          >
            <MenuItem value="">Sin certificar</MenuItem>
            {sourceOptions.map((source: any) => (
              <MenuItem key={source.key} value={source.key}>
                {source.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Localizador en la fuente"
          placeholder="Página, número o referencia editorial"
          value={item?.source_locator || ""}
          onChange={(e) =>
            patchItem(scope, sectionKey, optionOrder, {
              source_locator: e.target.value,
            })
          }
          fullWidth
        />

        <FormControl fullWidth>
          <InputLabel>Estado</InputLabel>
          <Select
            label="Estado"
            value={item?.status || EMPTY_STATUS}
            onChange={(e) =>
              patchItem(scope, sectionKey, optionOrder, {
                status: e.target.value as "draft" | "verified",
              })
            }
          >
            <MenuItem value="draft">Borrador / no certificado</MenuItem>
            <MenuItem value="verified">Verificado con fuente</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Rúbrica"
          value={item?.rubric || reference?.rubric || ""}
          onChange={(e) =>
            patchItem(scope, sectionKey, optionOrder, {
              rubric: e.target.value,
            })
          }
          multiline
          minRows={2}
          fullWidth
          style={{ gridColumn: "1 / -1" }}
        />
      </div>
    );
  };

  const mass = model?.scopes?.mass;
  const lectionary = model?.scopes?.lectionary;
  const hours = model?.scopes?.liturgy_of_hours;

  return (
    <div style={{ marginTop: 28 }}>
      <h3>Contenido oficial estructurado</h3>
      <p>
        Solo debe marcarse como <strong>verificado</strong> el texto cotejado
        contra una fuente litúrgica aprobada. Esta sección no genera ni
        completa textos automáticamente.
      </p>
      <div
        style={{
          margin: "0 0 18px",
          padding: 12,
          border: "1px solid #d7d7d7",
          borderRadius: 6,
          background: "#fafafa",
        }}
      >
        <strong>Cobertura de edición</strong>
        <div style={{ marginTop: 4 }}>
          {currentItems.length} campos estructurados cargados en este idioma.
          Texto, cita, rúbrica, fuente, localizador, estado, variante y opción
          quedan disponibles desde este editor.
        </div>
      </div>

      <FormControl fullWidth style={{ margin: "12px 0 28px" }}>
        <InputLabel>Variante litúrgica</InputLabel>
        <Select
          label="Variante litúrgica"
          value={variantKey}
          onChange={(e) => {
            setVariantKey(String(e.target.value));
            setMassFormularyOrder(1);
          }}
        >
          {variants.map((variant) => (
            <MenuItem key={variant.key} value={variant.key}>
              {variant.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {scopeEnabled("mass") && mass?.sections?.length > 0 && (
        <section>
          <h3>{mass.label || "Misa"}</h3>
          {massFormularyCount > 1 && (
            <FormControl fullWidth style={{ margin: "0 0 24px" }}>
              <InputLabel>Opción de Misa</InputLabel>
              <Select
                label="Opción de Misa"
                value={massFormularyOrder}
                onChange={(e) =>
                  setMassFormularyOrder(Number(e.target.value) || 1)
                }
              >
                {Array.from(
                  { length: massFormularyCount },
                  (_, index) => index + 1
                ).map((formularyOrder) => (
                  <MenuItem key={formularyOrder} value={formularyOrder}>
                    Opción {formularyOrder}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {mass.sections.map((section: any) => {
            const item = findItem(
              "mass",
              section.key,
              massFormularyOrder
            );
            return (
              <div
                key={`${section.key}-${massFormularyOrder}`}
                style={{ marginBottom: 30 }}
              >
                <label>{section.label}</label>
                <App
                  key={`structured-mass-${languageId}-${variantKey}-${massFormularyOrder}-${section.key}`}
                  placeholder={section.label}
                  content={item?.content_json || null}
                  htmlContent={item?.content_html || ""}
                  handleEditorChange={(htmlContent: any, jsonContent: any) =>
                    patchItem(
                      "mass",
                      section.key,
                      massFormularyOrder,
                      {
                        content_html: htmlContent,
                        content_json: jsonContent,
                      }
                    )
                  }
                />
                {metadataFields(
                  "mass",
                  section.key,
                  massFormularyOrder
                )}
              </div>
            );
          })}
        </section>
      )}

      {scopeEnabled("lectionary") && lectionary?.sections?.length > 0 && (
        <section>
          <h3>{lectionary.label || "Leccionario"}</h3>
          <p>
            Cada tipo de lectura admite varias opciones. Añadir una opción
            nunca elimina ni sustituye automáticamente otra.
          </p>
          {languageId === 1 && lectionaryReferenceSet && (
            <div
              style={{
                margin: "0 0 22px",
                padding: 12,
                border: "1px solid #ddd",
              }}
            >
              <strong>
                Mapa estructural OLM {lectionaryReferenceSet.olm_numbers}
              </strong>
              <div style={{ marginTop: 6 }}>
                Las referencias y el orden de opciones están precargados.
                Esto no certifica la redacción bíblica colombiana: el texto
                solo puede pasar a verificado después del cotejo con el
                Leccionario IV para Colombia.
              </div>
            </div>
          )}

          {lectionary.sections.map((section: any) => {
            const options = sectionOptions("lectionary", section.key);
            const nextOrder = Math.max(...options) + 1;

            return (
              <div key={section.key} style={{ marginBottom: 34 }}>
                <h4>{section.label}</h4>

                {options.map((optionOrder) => {
                  const item = findItem(
                    "lectionary",
                    section.key,
                    optionOrder
                  );

                  return (
                    <div
                      key={optionOrder}
                      style={{
                        padding: "12px 0 20px",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      <strong>Opción {optionOrder}</strong>
                      {expectedReference(
                        "lectionary",
                        section.key,
                        optionOrder
                      )?.olm_number && (
                        <span style={{ marginLeft: 10, color: "#666" }}>
                          OLM{" "}
                          {expectedReference(
                            "lectionary",
                            section.key,
                            optionOrder
                          )?.olm_number}
                        </span>
                      )}
                      <App
                        key={`structured-lectionary-${languageId}-${variantKey}-${section.key}-${optionOrder}`}
                        placeholder={`${section.label} · opción ${optionOrder}`}
                        content={item?.content_json || null}
                        htmlContent={item?.content_html || ""}
                        handleEditorChange={(
                          htmlContent: any,
                          jsonContent: any
                        ) =>
                          patchItem(
                            "lectionary",
                            section.key,
                            optionOrder,
                            {
                              content_html: htmlContent,
                              content_json: jsonContent,
                            }
                          )
                        }
                      />
                      {metadataFields(
                        "lectionary",
                        section.key,
                        optionOrder
                      )}
                    </div>
                  );
                })}

                {section.multiple && (
                  <Button
                    variant="outlined"
                    onClick={() =>
                      patchItem("lectionary", section.key, nextOrder, {})
                    }
                  >
                    Añadir opción
                  </Button>
                )}
              </div>
            );
          })}
        </section>
      )}

      {scopeEnabled("liturgy_of_hours") && hours?.sections?.length > 0 && (
        <section>
          <h3>{hours.label || "Liturgia de las Horas"}</h3>
          <p>
            Para idiomas distintos del español, estos campos solo deben
            verificarse cuando procedan de una edición nativa identificada.
            No se traducen desde el texto español.
          </p>

          {hours.sections.map((section: any) => {
            const options = sectionOptions(
              "liturgy_of_hours",
              section.key
            );

            return (
              <div key={section.key} style={{ marginBottom: 30 }}>
                <label>{section.label}</label>
                {options.map((optionOrder) => {
                  const item = findItem(
                    "liturgy_of_hours",
                    section.key,
                    optionOrder
                  );

                  return (
                    <div
                      key={`${section.key}-${optionOrder}`}
                      style={{ marginTop: 10 }}
                    >
                      {options.length > 1 && (
                        <strong>Opción {optionOrder}</strong>
                      )}
                      <App
                        key={`structured-hours-${languageId}-${variantKey}-${section.key}-${optionOrder}`}
                        placeholder={
                          options.length > 1
                            ? `${section.label} · opción ${optionOrder}`
                            : section.label
                        }
                        content={item?.content_json || null}
                        htmlContent={item?.content_html || ""}
                        handleEditorChange={(htmlContent: any, jsonContent: any) =>
                          patchItem(
                            "liturgy_of_hours",
                            section.key,
                            optionOrder,
                            {
                              content_html: htmlContent,
                              content_json: jsonContent,
                            }
                          )
                        }
                      />
                      {metadataFields(
                        "liturgy_of_hours",
                        section.key,
                        optionOrder
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}
